import { SvgIconUrl } from '..';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import svgIcons from '../../svgIcons';

type Props = {
  'data-test'?: string;
  value?: string;
  onChange?: (value: string) => void;
  onInteract?: (value: boolean) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  id?: string;
};

const FieldSearch = forwardRef<HTMLInputElement, Props>(
  ({ onChange, onInteract, 'data-test': dataTest, value, placeholder, autoFocus, className, id }, ref) => {
    const [isSearching, setIsSearching] = useState(false);
    const valueStreamRef = useRef<string>(value ?? '');
    const [internalValue, setInternalValue] = useState(value ?? '');
    const debouncedValue = useDebounce(internalValue, 500);

    useEffect(() => {
      valueStreamRef.current = debouncedValue;
      onChange?.(debouncedValue);
    }, [debouncedValue, onChange]);

    useEffect(() => {
      setInternalValue(value ?? '');
    }, [value]);

    useEffect(() => {
      onInteract?.(isSearching);
    }, [isSearching, onInteract]);

    const _wrapperClassName = useMemo(() => `field-wrapper ${className}-wrapper`, [className]);
    const _className = useMemo(() => `field field-search ${className}`, [className]);

    const onClearField = useCallback(() => setInternalValue(''), []);

    const onFocus = useCallback(() => setIsSearching(true), []);

    const onBlur = useCallback(() => setIsSearching(false), []);

    const _onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setInternalValue(e.target.value), []);

    const onInput = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => setInternalValue(e.currentTarget.value),
      [],
    );

    return (
      <div className={_wrapperClassName} data-test={dataTest} id={id}>
        <input
          ref={ref}
          id='search-field'
          value={internalValue}
          autoFocus={autoFocus}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={_onChange}
          onInput={onInput}
          placeholder={placeholder}
          className={_className}
          type='text'
          autoComplete='off'
          data-test='search-input'
        />
        <div className='icon-search-in-field'>
          <SvgIconUrl symbol={svgIcons.Search()} />
        </div>
        {internalValue && <SvgIconUrl onClick={onClearField} className='icon-s icon-clear' symbol={svgIcons.Clear()} />}
      </div>
    );
  },
);

export default React.memo(FieldSearch);
